import { useEffect, useRef, useState } from "react";
import { api } from "../services/api";

const SEARCH_OPTIONS = Object.freeze([
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" }
]);

function display(value) {
  return String(value || "").trim() || "—";
}

function errorCopy(error) {
  if (error?.code === "ROOTS_INTAKE_SUBMIT_TRANSACTIONS_REQUIRED" || error?.status === 403) {
    return {
      title: "Transaction intake access denied.",
      detail: "Your ROOTS account is not authorized to search Follow Up Boss for transaction intake."
    };
  }
  if (error?.code === "ROOTS_FUB_PEOPLE_RATE_LIMITED" || error?.status === 429) {
    return {
      title: "Follow Up Boss is rate limiting search.",
      detail: "Retry the live search after the rate limit clears. No fallback data is available."
    };
  }
  return {
    title: "Follow Up Boss search is unavailable.",
    detail: "Retry the live read. ROOTS will not substitute fictional or cached People data."
  };
}

export default function FubPrimaryPersonSearch({ selected, label, disabled = false, onSelect }) {
  const [field, setField] = useState("name");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("initial");
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const requestSequence = useRef(0);
  const controller = useRef(null);

  useEffect(() => () => controller.current?.abort(), []);

  async function runSearch(event) {
    event?.preventDefault?.();
    const normalized = query.trim();
    if (!normalized || disabled) {
      controller.current?.abort();
      requestSequence.current += 1;
      setResults([]);
      setError(null);
      setStatus("initial");
      return;
    }

    controller.current?.abort();
    const activeController = new AbortController();
    controller.current = activeController;
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    setStatus("loading");
    setResults([]);
    setError(null);

    try {
      const response = await api.searchFubPeople({
        field,
        query: normalized,
        signal: activeController.signal
      });
      if (sequence !== requestSequence.current || activeController.signal.aborted) return;
      const items = Array.isArray(response?.items) ? response.items : [];
      setResults(items);
      setStatus(items.length ? "results" : "zero");
    } catch (searchError) {
      if (activeController.signal.aborted || sequence !== requestSequence.current || searchError?.name === "AbortError") return;
      setError(searchError);
      setStatus("error");
    }
  }

  function changeSelection() {
    controller.current?.abort();
    requestSequence.current += 1;
    setResults([]);
    setError(null);
    setStatus("initial");
    onSelect(null);
  }

  if (selected) {
    return (
      <div className="r2b-primary-client-selection">
        <div className="r2b-selected-client">
          <div>
            <strong>{label}: {display(selected.name)}</strong>
            <span>{display(selected.email)} · {display(selected.phone)}</span>
            <small>FUB Person ID: {display(selected.id)}</small>
          </div>
          {!disabled && <button type="button" className="button secondary" onClick={changeSelection}>Change selection</button>}
        </div>
        <div className="r2b-derived-grid">
          <div><span>Assigned Agent · FUB-derived</span><strong>{display(selected.assignedAgent?.name)}</strong></div>
          <div><span>Lead Source · FUB-derived</span><strong>{display(selected.leadSource)}</strong></div>
        </div>
      </div>
    );
  }

  const copy = errorCopy(error);
  return (
    <div className="r2b-primary-client-search">
      <form className="r2b-primary-search-form" onSubmit={runSearch}>
        <label className="r2b-field">
          <span>Search by</span>
          <select value={field} disabled={disabled || status === "loading"} onChange={event => setField(event.target.value)}>
            {SEARCH_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label className="r2b-field r2b-primary-search-query">
          <span>{field === "email" ? "Email" : field === "phone" ? "Phone" : "Name"}</span>
          <input
            value={query}
            disabled={disabled || status === "loading"}
            autoComplete="off"
            onChange={event => setQuery(event.target.value)}
            placeholder={`Search Follow Up Boss by ${field}`}
          />
        </label>
        <button type="submit" className="button primary" disabled={disabled || status === "loading" || !query.trim()}>
          {status === "loading" ? "Searching…" : "Search FUB"}
        </button>
      </form>

      {status === "initial" && <p className="r2b-support">Enter a name, email, or phone number. No People request is made until you submit a nonblank search.</p>}
      {status === "loading" && <div className="r2b-notice"><strong>Searching Follow Up Boss…</strong><br />Waiting for the current live read.</div>}
      {status === "zero" && <div className="r2b-notice r2b-notice--warn"><strong>No FUB People matched this search.</strong><br />Change the search value or search field and try again.</div>}
      {status === "error" && <div className="r2b-notice r2b-notice--error"><strong>{copy.title}</strong><br />{copy.detail}<div style={{ marginTop: 10 }}><button type="button" className="button secondary" onClick={() => runSearch()}>Retry live search</button></div></div>}
      {status === "results" && (
        <div className="r2b-search-results" aria-live="polite">
          <p className="r2b-support">Select the Primary Client explicitly. ROOTS never auto-selects a search result.</p>
          {results.map(person => (
            <button key={person.id} type="button" className="r2b-search-result" disabled={disabled} onClick={() => onSelect(person)}>
              <span>
                <strong>{display(person.name)}</strong>
                <small>{display(person.email)} · {display(person.phone)} · Agent: {display(person.assignedAgent?.name)} · Source: {display(person.leadSource)}</small>
              </span>
              <b>Select</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
